/**
 * WAYP-316 / i18n foundation — react-i18next.
 *
 * Architecture (see docs/i18n.md):
 *   - ONE i18next namespace (`translation`). Every source file under
 *     `src/locales/<lng>/<name>.json` is merged in under the top-level key
 *     `<name>` — so `locales/en/loads.json` → `t('loads.title')`.
 *   - English (`src/locales/en/`) is the ONLY human/AI-edited source. Target
 *     locales (`fr-CA/`, later `es/`) are GENERATED by `scripts/i18n-translate.mjs`
 *     and must never be hand-edited.
 *   - Files are discovered automatically with Vite's `import.meta.glob` — adding
 *     a new `en/<feature>.json` needs no registration here.
 *   - Resources are bundled eagerly (not lazy-loaded): for an app this size the
 *     locale JSON is a rounding error next to ag-grid/mapbox, and eager loading
 *     removes a whole class of Suspense/loading edge cases. If bundle size ever
 *     matters, switching to a lazy backend is a change contained to this file.
 */

import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

import languages from '../../locales/languages.json';

export const LANGUAGE_KEY = 'waypoint_language';

/** Language config is the single source of truth (locales/languages.json). */
export const SOURCE_LANGUAGE = languages.source;
export const SUPPORTED_LANGUAGES = [languages.source, ...languages.targets].map(
  (code) => ({ code, label: (languages.labels as Record<string, string>)[code] ?? code }),
);
const SUPPORTED_CODES = new Set(SUPPORTED_LANGUAGES.map((l) => l.code));

// Eagerly import every per-feature locale file and merge each into its
// language's single `translation` namespace, keyed by filename.
type JsonModule = { default: Record<string, unknown> };
const localeModules = import.meta.glob<JsonModule>('../../locales/*/*.json', {
  eager: true,
});

const resources: Record<string, { translation: Record<string, unknown> }> = {};
for (const [filePath, mod] of Object.entries(localeModules)) {
  const match = filePath.match(/\/locales\/([^/]+)\/([^/]+)\.json$/);
  if (!match) continue;
  const [, lng, name] = match;
  (resources[lng] ??= { translation: {} }).translation[name] =
    (mod as JsonModule).default ?? mod;
}

export function getStoredLanguage(): string {
  try {
    const stored = localStorage.getItem(LANGUAGE_KEY);
    if (stored && SUPPORTED_CODES.has(stored)) return stored;
  } catch {
    /* localStorage unavailable — fall through to default */
  }
  return SOURCE_LANGUAGE;
}

/** Keep <html lang="…"> in sync so the browser/AT and CSS `:lang()` are correct. */
function syncHtmlLang(lang: string): void {
  try {
    document.documentElement.lang = lang;
  } catch {
    /* SSR / no document — ignore */
  }
}

/**
 * Change the active language and remember it locally. Persisting the choice to
 * the user's profile (User.language) is done by the caller (see
 * `useLanguagePreference`) so this module stays free of API/store imports.
 */
export function setStoredLanguage(lang: string): void {
  if (!SUPPORTED_CODES.has(lang)) return;
  try {
    localStorage.setItem(LANGUAGE_KEY, lang);
  } catch {
    /* ignore storage write failures */
  }
  syncHtmlLang(lang);
  void i18n.changeLanguage(lang);
}

const initialLanguage = getStoredLanguage();

i18n.use(initReactI18next).init({
  resources,
  lng: initialLanguage,
  fallbackLng: SOURCE_LANGUAGE,
  supportedLngs: [...SUPPORTED_CODES],
  defaultNS: 'translation',
  interpolation: {
    escapeValue: false, // React already escapes
  },
  returnEmptyString: false, // empty target value → fall back to English
});

syncHtmlLang(initialLanguage);

export default i18n;
